import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer, getWarehouseParams } from '../utils/warehouseParams';
import { registerClearable } from '../utils/cacheRegistry';

/**
 * The global warehouse filter is injected into GET requests by the axios
 * interceptor (applyWarehouseFilter), so it changes the actual response.
 * Cache keys must therefore include the selected warehouse ids — otherwise a
 * cached list from a previous warehouse selection is served after the filter
 * changes, and the filter appears to "not work".
 */
function warehouseCacheSuffix() {
  const ids = getWarehouseParams().warehouse_ids;
  return Array.isArray(ids) && ids.length ? `::wh:${[...ids].sort().join(',')}` : '';
}

// Axios instance for warehouse-inventory API
const inventoryAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
inventoryAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
inventoryAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

const BASE_URL = '/warehouse-inventory';

/**
 * When a request uses responseType:'blob', an error response body is also a
 * Blob (not parsed JSON). Read it back to JSON and extract a readable message.
 */
async function blobErrorMessage(error) {
  try {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      const json = JSON.parse(text);
      return json?.error?.message || json?.message || null;
    }
  } catch (e) {
    // fall through to caller's generic extractor
  }
  return null;
}

// Caching helpers (memory + sessionStorage)
// v2: products-view now pages alphabetically instead of by id. Cached v1 pages hold
// the old ordering, so bump the prefix to retire them instead of serving a stale,
// unsorted list for up to the TTL after this ships.
const CACHE_PREFIX = 'erp_warehouseinventory_cache_v2';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}

// A cached full list is either a bare array or a paginated `{ items, total }`
// envelope (the newer list endpoints). The modifier used to run only on bare
// arrays, so an envelope kept serving the stale row after an update/delete (P1-4).
function applyListModifier(value, modifierFn) {
  if (Array.isArray(value)) return modifierFn(value.slice());
  if (value && Array.isArray(value.items)) {
    const items = modifierFn(value.items.slice());
    const delta = items.length - value.items.length;
    const total = typeof value.total === 'number' ? Math.max(0, value.total + delta) : value.total;
    return { ...value, items, total };
  }
  return null;
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const next = applyListModifier(entry?.value, modifierFn);
        if (next !== null) writeMemoryCache(key, next);
      }
    }
  } catch (e) { }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const next = applyListModifier(parsed?.value, modifierFn);
          if (next !== null) {
            parsed.value = next;
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) { }
      }
    }
  } catch (e) { }
}


const warehouseInventoryService = {
  /**
   * Import opening stock from an Excel file.
   *
   * The endpoint is all-or-nothing: if any row is bad it writes nothing and returns a
   * line-numbered list, so a rejected file leaves the warehouse exactly as it was.
   * Those come back as `errors`, which the shared ImportModal renders as its list.
   */
  async importInventory(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await inventoryAPI.post(
        '/warehouse-inventory/import/excel',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const data = response.data || {};
      if (data.success === false) {
        // A refusal, not a transport failure — hand the row errors to the modal.
        return { success: false, error: data.errors || [], data };
      }

      // Stock changed, so every cached inventory list is now stale.
      this.clearCache?.();
      return { success: true, data };
    } catch (error) {
      console.error('Import inventory error:', error.response?.data || error.message || error);
      const detail = error?.response?.data?.errors;
      return {
        success: false,
        error: Array.isArray(detail) && detail.length ? detail : extractApiError(error),
      };
    }
  },

  // 1. Get Warehouse Inventory (with filters)
  async getAllInventory(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}${warehouseCacheSuffix()}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      // Pass params through the axios config (not a hand-built URL) so the
      // applyWarehouseFilter interceptor can inject the global warehouse_ids and
      // warehouseParamsSerializer emits ?warehouse_ids=1&warehouse_ids=3.
      const requestParams = {};
      if (params.skip !== undefined) requestParams.skip = params.skip;
      if (params.limit !== undefined) requestParams.limit = params.limit;
      if (params.search) requestParams.search = params.search;
      // Sub Category (product catalogue) filter — repeated ?catalogue_ids=17&catalogue_ids=21.
      // Empty/absent means "all sub categories" (the serializer drops empty arrays).
      if (Array.isArray(params.catalogue_ids) && params.catalogue_ids.length > 0) {
        requestParams.catalogue_ids = params.catalogue_ids;
      }

      const response = await inventoryAPI.get(`${BASE_URL}/products-view`, { params: requestParams });
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 2. Get Low Stock Items
  async getLowStock(params = {}, options = {}) {
    const { force = false } = options;
    const key = `low-stock::${JSON.stringify(params || {})}${warehouseCacheSuffix()}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const response = await inventoryAPI.get(`${BASE_URL}/low-stock`, { params });
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 3. Get Product Stock Distribution
  async getProductDistribution(product_size_id, options = {}) {
    const { force = false } = options;
    const key = `product::${product_size_id}::distribution`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const response = await inventoryAPI.get(`${BASE_URL}/product/${product_size_id}/distribution`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 4. Get Warehouse Stock Summary
  async getWarehouseSummary(warehouse_id, options = {}) {
    const { force = false } = options;
    const key = `warehouse::${warehouse_id}::summary`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const response = await inventoryAPI.get(`${BASE_URL}/warehouse/${warehouse_id}/summary`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 5. Get Inventory Statistics
  async getStats(options = {}) {
    const { force = false } = options;
    const key = `stats::all${warehouseCacheSuffix()}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      const response = await inventoryAPI.get(`${BASE_URL}/stats`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 6. Get Inventory Record by ID
  async getInventoryById(id, options = {}) {
    const { force = false } = options;
    const key = `byId::${id}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
      // fallback: search in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((i) => String(i.id) === String(id));
        if (found) { writeMemoryCache(key, found); writeCache(key, found); return { success: true, data: found }; }
      }
    }

    try {
      const response = await inventoryAPI.get(`${BASE_URL}/${id}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 7. Create Warehouse Inventory (with multiple products)
  // Payload structure: { warehouse_id, products: [{ product_size_id, stock_quantity, minimum_stock, pending_order }, ...] }
  async createInventory(payload) {
    try {
      const response = await inventoryAPI.post(BASE_URL, payload);
      const created = response.data;

      // If response is an array of created inventory records, handle multiple entries
      if (Array.isArray(created)) {
        updateFullListCachesWithModifier((arr) => {
          const newArr = [...created, ...arr];
          // Remove duplicates by id
          const seen = new Set();
          return newArr.filter((i) => {
            const key = String(i.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });

        // Cache each created inventory by id
        for (const inv of created) {
          try { const key = `byId::${inv.id}`; writeMemoryCache(key, inv); writeCache(key, inv); } catch (e) { }
        }

        return { success: true, data: created };
      } else {
        // Single record response (backward compatibility)
        updateFullListCachesWithModifier((arr) => {
          const exists = arr.some((i) => String(i.id) === String(created.id));
          if (exists) return arr;
          return [created, ...arr];
        });

        try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }

        return { success: true, data: created };
      }
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 8. Adjust Stock (Add / Remove)
  async adjustStock(id, payload) {
    try {
      const response = await inventoryAPI.post(`${BASE_URL}/${id}/adjust`, payload);
      const updated = response.data;

      // update caches
      try { const key = `byId::${id}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(id) ? updated : i)));

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 9. Transfer Stock
  async transferStock(payload) {
    try {
      const response = await inventoryAPI.post(`${BASE_URL}/transfer`, payload);
      const data = response.data;

      // transfer may affect multiple warehouse summaries / lists; best-effort cache update
      try {
        // If response contains updated records, update byId caches
        if (Array.isArray(data.updated) && data.updated.length) {
          for (const upd of data.updated) {
            try { const key = `byId::${upd.id}`; writeMemoryCache(key, upd); writeCache(key, upd); } catch (e) { }
          }
          updateFullListCachesWithModifier((arr) => {
            const map = new Map(data.updated.map((u) => [String(u.id), u]));
            return arr.map((i) => map.get(String(i.id)) || i);
          });
        }
      } catch (e) { }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 10. Update Warehouse Inventory (bulk update per product across warehouses)
  // New API: POST /warehouse-inventory/product/bulk-update-warehouses
  // body: { product_size_id, warehouse_updates: [{ warehouse_id, stock_quantity, minimum_stock, ... }, ...] }
  async updateInventory(product_size_id, payload) {
    try {
      const endpoint = `${BASE_URL}/product/bulk-update-warehouses`;

      // Normalize payload into expected body shape
      let body = null;
      if (payload && Array.isArray(payload.warehouse_updates)) {
        body = { product_size_id, warehouse_updates: payload.warehouse_updates };
      } else if (payload && payload.warehouse_id) {
        body = {
          product_size_id: product_size_id || payload.product_size_id || null,
          warehouse_updates: [
            {
              warehouse_id: payload.warehouse_id,
              stock_quantity: payload.stock_quantity,
              minimum_stock: payload.minimum_stock,
            },
          ],
        };
      } else {
        // fallback: if caller passed only product_size_id and expects server-side behaviour
        body = { product_size_id, warehouse_updates: [] };
      }

      const response = await inventoryAPI.put(endpoint, body);
      const data = response.data;

      // The API may return a structure containing updated records or a direct array.
      // Try to update caches conservatively based on returned shape.
      try {
        if (data && Array.isArray(data.updated) && data.updated.length) {
          // Update per-record caches and the full-list
          for (const upd of data.updated) {
            try { const key = `byId::${upd.id}`; writeMemoryCache(key, upd); writeCache(key, upd); } catch (e) { }
          }
          updateFullListCachesWithModifier((arr) => {
            const map = new Map(data.updated.map((u) => [String(u.id), u]));
            return arr.map((i) => map.get(String(i.id)) || i);
          });
        } else if (Array.isArray(data) && data.length) {
          // API returned an array of updated inventory records
          for (const inv of data) {
            try { const key = `byId::${inv.id}`; writeMemoryCache(key, inv); writeCache(key, inv); } catch (e) { }
          }
          updateFullListCachesWithModifier((arr) => {
            const map = new Map(data.map((u) => [String(u.id), u]));
            return arr.map((i) => map.get(String(i.id)) || i);
          });
        } else if (data && data.id) {
          // Single updated record
          try { const key = `byId::${data.id}`; writeMemoryCache(key, data); writeCache(key, data); } catch (e) { }
          updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(data.id) ? data : i)));
        }
      } catch (e) { }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 11. Delete Warehouse Inventory
  async deleteInventory(id) {
    try {
      const response = await inventoryAPI.delete(`${BASE_URL}/${id}`);

      updateFullListCachesWithModifier((arr) => arr.filter((i) => String(i.id) !== String(id)));
      try { deleteCacheEntry(`byId::${id}`); } catch (e) { }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Export warehouse inventory as a CSV pivoted product-row × warehouse-column,
   * filtered to one or more Sub Categories (product_catalogue_id).
   * SUPER_ADMIN only. Returns the raw blob + headers so the caller can trigger
   * a file download.
   *
   * @param {Object} params
   * @param {Array<number>} [params.catalogue_ids] Sub Category ids to include (omit = all)
   * @param {Array<number>} [params.warehouse_ids] Restrict warehouse columns (omit = user's scope)
   * @param {'stock'|'available'} [params.value] Which quantity each cell shows
   */
  async exportBySubcategory(params = {}) {
    try {
      // Build the query string explicitly (like productCatalogue export) so the
      // global warehouse filter injected by `applyWarehouseFilter` — which only
      // touches config.params, not the URL — does not clobber the caller's
      // explicit warehouse selection.
      const queryParams = new URLSearchParams();

      (params.catalogue_ids || []).forEach((id) => {
        if (id !== undefined && id !== null && id !== '') queryParams.append('catalogue_ids', String(id));
      });
      (params.warehouse_ids || []).forEach((id) => {
        if (id !== undefined && id !== null && id !== '') queryParams.append('warehouse_ids', String(id));
      });
      if (params.value) queryParams.append('value', params.value);

      const query = queryParams.toString();
      const url = `${BASE_URL}/export/by-subcategory${query ? `?${query}` : ''}`;
      // skipWarehouseFilter: the modal's warehouse selection is authoritative.
      // Without this, applyWarehouseFilter would append the global SideNav
      // warehouse_ids on top of ours, widening/overriding the export scope.
      const response = await inventoryAPI.get(url, { responseType: 'blob', skipWarehouseFilter: true });

      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      // On error the body is a Blob even though we requested one — parse it back.
      const msg = await blobErrorMessage(error);
      return { success: false, error: msg || extractApiError(error) };
    }
  },

  clearCache() {
    try {
      // Memory keys are "all::…" / "byId::…" — the old substring match never hit, so
      // the in-memory list outlived clearCache() and kept serving stale rows (P1-4).
      memoryCache.clear();
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { }
  }
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => warehouseInventoryService.clearCache());

export default warehouseInventoryService;
